import * as React from "react";
import "hammerjs";
import {
  Chart,
  ChartValueAxisTitle,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartLegend,
  ChartTooltip,
  SharedTooltipContext,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartCategoryAxisItemProps,
  ChartSeriesItemProps,
  ChartValueAxisItemProps,
  ChartLegendProps,
  ChartArea,
  RenderEvent,
  SeriesClickEvent,
} from "@progress/kendo-react-charts";
import { defaultValueAxisFormatter } from "../../utils/defaultFormatters";
import { TooltipContext } from "@progress/kendo-react-charts";
import { ColumnChartTooltipRenderer } from "../ColumnChartTooltip/ColumnChartTooltip";
import { CSSProperties } from "react";

export interface ColumnChartProps {
  items: Serie[];
  valueAxisTitle?: string;
  valueAxisFormatter?(e?: number): string;
  tooltipRenderer?(
    context: TooltipContext | SharedTooltipContext,
    valueAxisFormatter: (e?: number) => string
  ): React.ReactElement;
  chartStyle?: CSSProperties;
  categories?: string[];
  stacked?: boolean;
  legendVisible?: boolean;
  className?: string;
  valueAxisVisible?: boolean;
  categoryAxisProps?: ChartCategoryAxisItemProps;
  valueAxisProps?: ChartValueAxisItemProps;
  seriesGap?: number;
  chartSeriesItemProps?: ChartSeriesItemProps;
  tooltipVisible?: boolean;
  min?: number;
  max?: number;
  legendProps?: ChartLegendProps;
  onRender?: ((event: RenderEvent) => void) | undefined;
  generating?: boolean;
  height?: number;
  width?: number;
  onSeriesItemClick?: (event: SeriesClickEvent) => void;
}

export type Serie = {
  name: string;
  values: number[];
  color: string;
};

export const ColumnChart = ({
  generating = false,
  items,
  valueAxisTitle,
  valueAxisFormatter = defaultValueAxisFormatter,
  tooltipRenderer = ColumnChartTooltipRenderer,
  chartStyle,
  categories,
  stacked = false,
  legendVisible = false,
  valueAxisVisible = true,
  className,
  categoryAxisProps,
  seriesGap,
  chartSeriesItemProps,
  valueAxisProps,
  tooltipVisible = true,
  min,
  max,
  legendProps,
  onRender,
  height,
  width,
  onSeriesItemClick,
}: ColumnChartProps) => {
  return (
    // @ts-ignore
    <Chart
      className={className}
      style={{ ...chartStyle, height, width: width ?? "100%" }}
      onRender={(v) => onRender?.(v)}
      onSeriesClick={(e) => onSeriesItemClick?.(e)}
    >
      <ChartArea background={"transparent"} height={height} width={width} />
      <ChartLegend
        position={"top"}
        visible={legendVisible}
        orientation={"horizontal"}
        {...legendProps}
      />
      <ChartTooltip
        opacity={0.9}
        color={"var(--tooltip-color)"}
        background={"var(--tooltip-background-color)"}
        render={(context: any) => tooltipRenderer(context, valueAxisFormatter)}
        visible={tooltipVisible}
      />
      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          majorGridLines={{
            color: generating ? undefined : "var(--muted-border-color)",
            width: 0.1,
          }}
          labels={{
            color: "var(--color)",
          }}
          categories={categories}
          name={"categoryAxis"}
          {...categoryAxisProps}
        />
      </ChartCategoryAxis>
      <ChartSeries>
        {items?.map((serie) => (
          <ChartSeriesItem
            stack={stacked}
            type="column"
            gap={seriesGap}
            data={serie.values}
            name={serie.name}
            color={serie.color}
            border={{ width: 0 }}
            {...chartSeriesItemProps}
          />
        ))}
      </ChartSeries>
      <ChartValueAxis>
        <ChartValueAxisItem
          majorGridLines={{
            color: generating ? undefined : "var(--muted-color)",
            width: 0.1,
          }}
          min={min}
          max={max}
          visible={valueAxisVisible}
          labels={{
            content: (e: any) => valueAxisFormatter(e.value),
            color: "var(--color)",
          }}
          {...valueAxisProps}
        >
          {valueAxisTitle && <ChartValueAxisTitle text={valueAxisTitle} />}
        </ChartValueAxisItem>
      </ChartValueAxis>
    </Chart>
  );
};

export default ColumnChart;
